import { useEffect, useState } from 'react';
import { Plus } from 'react-feather';
import { Link, useNavigate } from 'react-router-dom';

import { CartData } from '@/Cart/types/CartTypes';
import AddAddressModal from '@/MyPage/components/AddAddressModal';
import { AddressData } from '@/MyPage/types/AddressTypes';
import Button from '@/shared/components/UI/Button';
import Input from '@/shared/components/UI/Input';
import Modal from '@/shared/components/UI/Modal';
import apiToken, {
  handleApiError,
  isSuccess,
} from '@/shared/services/axiosInstance';
import { useCartStore } from '@/shared/stores/useCartStore';
import { useUserStore } from '@/shared/stores/useUserStore';
import {
  formatNumber,
  formatPrice,
  getTotalPrice,
  roundPrice,
} from '@/shared/utils/priceUtils';

import { useValidateOrder } from '../hooks/useValidateOrder';
import { OrderData, ReceiverInfo } from '../types/OrderTypes';
import UserAddressList from './UserAddressList';

const Order = () => {
  const navigate = useNavigate();
  const validatedCartData = useCartStore((state) => state.validatedCartData);
  const { userData, fetchUserData } = useUserStore();

  const [cartData, setCartData] = useState<CartData | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isAddressList, setIsAddressList] = useState(false);
  const [isAddAddress, setIsAddAddress] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressData | undefined>(
    undefined,
  );
  const [receiverInfo, setReceiverInfo] = useState<ReceiverInfo>({
    companyName: '',
    companyEmail: '',
    receiverName: '',
    postalCode: '',
    addressDetail: '',
    phoneNumber: '',
  });
  const [orderData, setOrderData] = useState<OrderData>({
    currencyCode: '',
    finalPaymentAmount: 0,
    receiverInfo,
    productOptionList: [],
  });
  const { validateOrder, errors } = useValidateOrder();

  const handleReceiverInfoChange = (
    field: keyof ReceiverInfo,
    value: string,
  ) => {
    setReceiverInfo((prev) => ({
      ...prev,
      [field]: value || '',
    }));
    // Update orderData as well
    setOrderData((prev) => ({
      ...prev,
      receiverInfo: {
        ...prev.receiverInfo,
        [field]: value || '',
      },
    }));
  };

  const handleAddressSelect = (address: AddressData) => {
    setReceiverInfo((prev) => ({
      ...prev,
      postalCode: address.postalCode || '',
      addressDetail: address.addressDetail || '',
    }));
    setOrderData((prev) => ({
      ...prev,
      receiverInfo: {
        ...prev.receiverInfo,
        postalCode: address.postalCode || '',
        addressDetail: address.addressDetail || '',
      },
    }));
  };

  const handleAddAddressFromOrder = () => {
    setIsAddressList(false);
    setEditingAddress(undefined);
    setIsAddAddress(true);
  };

  const handleEditAddressFromOrder = (address: AddressData) => {
    setIsAddressList(false);
    setEditingAddress(address);
    setIsAddAddress(true);
  };

  const handleRefreshData = () => {
    fetchUserData();
  };

  const createOrder = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const finalReceiverInfo = {
      ...receiverInfo,
      companyName: userData?.companyName || '',
    };

    const isValid = validateOrder({
      receiverInfo: finalReceiverInfo,
      businessRegistrationNumber: userData?.businessRegistrationNumber || '',
    });

    if (!isValid) {
      return;
    }

    if (!cartData || cartData.cartList.length === 0) {
      alert('Cart is empty');
      return;
    }

    if (window.confirm('Do you want to place the order?')) {
      try {
        const response = await apiToken.post('/api/v1/order', {
          ...orderData,
          receiverInfo: finalReceiverInfo,
        });

        if (isSuccess(response.data)) {
          alert('Order has been placed successfully.');

          useCartStore.getState().clearCartData();

          setOrderId(response.data.data.uuid);
        }
        // Handle successful order creation (e.g., navigate to confirmation page)
      } catch (error) {
        const errorMessage = handleApiError(error);
        console.error('Error creating order:', errorMessage);
        alert('Failed to place order. Please try again.');
      }
    }
  };

  useEffect(() => {
    if (orderId) {
      return;
    }

    if (!validatedCartData) {
      alert(
        'We could not retrieve your latest cart information.\n' +
          'You will be redirected to the cart page to review your cart again.',
      );

      navigate('/cart');
      return;
    }

    setCartData(validatedCartData);

    setOrderData((prev) => ({
      ...prev,
      currencyCode: validatedCartData.currencyCode,
      finalPaymentAmount: roundPrice(
        validatedCartData.totalPrice,
        validatedCartData.currencyCode,
      ),
      productOptionList: validatedCartData.cartList.map((item) => ({
        productOptionUuid: item.productOption.uuid,
        quantity: item.quantity,
      })),
    }));
  }, [validatedCartData, navigate, orderId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);
  return (
    <>
      {/* Modal: Assigning a person in charge */}
      {orderId && (
        <Modal
          isOpen={!!orderId}
          onClose={() => setOrderId(null)}
          title="Assigning a person in charge"
          size="md"
          titleAlign="center"
        >
          <p className="mx-auto max-w-[320px] text-[14px] font-normal leading-[1.4] text-[#222]">
            After checking your order, the person in charge will contact you for
            the receipt and the tax invoice.
          </p>
          <Link to={`/mypage/shoppingdetail/${orderId}`}>
            <Button variant="primary" size="md" fullWidth className="mt-6">
              Confirm
            </Button>
          </Link>
        </Modal>
      )}

      {/* Modal: Select Address */}
      <UserAddressList
        isOpen={isAddressList}
        onClose={() => setIsAddressList(false)}
        onAddressSelect={handleAddressSelect}
        onAddAddress={handleAddAddressFromOrder}
        onEditAddress={handleEditAddressFromOrder}
        onRefreshData={handleRefreshData}
        userData={userData}
      />

      {/* Modal: Add Address */}
      {isAddAddress && (
        <AddAddressModal
          onClose={() => {
            setIsAddAddress(false);
            setEditingAddress(undefined);
          }}
          onAddressAdded={() => {
            setIsAddAddress(false);
            setEditingAddress(undefined);
            handleRefreshData();
            setIsAddressList(true);
          }}
          editAddress={editingAddress}
        />
      )}

      <section className="mx-auto flex max-w-[57.5rem] justify-between">
        <div className="w-full max-w-[31.5rem] pt-[68px]">
          <h3 className="mb-10 text-center text-[24px] font-extrabold text-[#95699f]">
            Delivery
          </h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsAddressList(true)}
            leftIcon={<Plus size={20} color="#95699f" strokeWidth={1.5} />}
            className="w-full justify-end px-0 font-normal text-[#95699f] [&>span:first-child]:gap-1"
          >
            Select / Add Address
          </Button>
          <form method="post" onSubmit={createOrder} className="mb-[100px]">
            <div className="mx-auto my-7 max-w-[34.25rem]">
              <Input
                type="number"
                name="businessNumber"
                label="Business Registration Number"
                value={userData?.businessRegistrationNumber || ''}
                onChange={() => {}}
                placeholder="Business registration number"
                error={errors.businessRegistrationNumber}
                labelClassName="text-[16px] text-[#4a4a4a]"
                className="mt-2"
              />
            </div>
            <div className="mx-auto my-7 max-w-[34.25rem]">
              <Input
                type="text"
                name="name"
                label="Company Name"
                value={userData?.companyName || ''}
                onChange={() => {}}
                placeholder="Company Name"
                error={errors.companyName}
                labelClassName="text-[16px] text-[#4a4a4a]"
                className="mt-2"
              />
            </div>
            <div className="mx-auto my-7 max-w-[34.25rem]">
              <Input
                type="text"
                name="receiverName"
                label="Receiver Name"
                value={receiverInfo.receiverName}
                onChange={(e) =>
                  handleReceiverInfoChange('receiverName', e.target.value)
                }
                placeholder="Receiver Name"
                error={errors.receiverName}
                labelClassName="text-[16px] text-[#4a4a4a]"
                className="mt-2"
              />
            </div>
            <div className="mx-auto my-7 max-w-[34.25rem]">
              <Input
                type="email"
                name="email"
                label="Receiver Email"
                value={receiverInfo.companyEmail}
                onChange={(e) =>
                  handleReceiverInfoChange('companyEmail', e.target.value)
                }
                placeholder="Receiver Email"
                error={errors.companyEmail}
                labelClassName="text-[16px] text-[#4a4a4a]"
                className="mt-2"
              />
            </div>
            <div className="mx-auto my-7 max-w-[34.25rem]">
              <Input
                type="tel"
                name="tel"
                label="Phone Number"
                value={receiverInfo.phoneNumber}
                onChange={(e) =>
                  handleReceiverInfoChange('phoneNumber', e.target.value)
                }
                placeholder="e.g. +00 000000000"
                error={errors.phoneNumber}
                labelClassName="text-[16px] text-[#4a4a4a]"
                className="mt-2"
              />
            </div>
            <div className="flex justify-between gap-3 rounded-lg bg-[#f4ecf4] p-4">
              <img src="./images/alert-circle.svg" alt="x" />
              <div>
                <p className="text-[12px] font-medium leading-[140%] text-[#95699f]">
                  When filling in address, Please write only Korean in Korea,
                  only Chinese in China and Taiwan, and please write in English
                  except for Korea, China, and Taiwan.
                </p>
                <p className="text-[12px] font-medium leading-[140%] text-[#95699f]">
                  We are not responsible for any delivery errors due to
                  incorrect address entry.
                </p>
              </div>
            </div>
            <div className="mx-auto my-7 max-w-[34.25rem]">
              <Input
                type="text"
                name="postalCode"
                label="Postal Code"
                value={receiverInfo.postalCode}
                onChange={(e) =>
                  handleReceiverInfoChange('postalCode', e.target.value)
                }
                placeholder="Postal code (optional)"
                labelClassName="text-[16px] text-[#4a4a4a]"
                className="mt-2"
              />
            </div>
            <div className="mx-auto my-7 max-w-[34.25rem]">
              <Input
                type="text"
                name="address"
                label="Address"
                value={receiverInfo.addressDetail}
                onChange={(e) =>
                  handleReceiverInfoChange('addressDetail', e.target.value)
                }
                placeholder="Address"
                error={errors.addressDetail}
                labelClassName="text-[16px] text-[#4a4a4a]"
                className="mt-2"
              />
            </div>
            <Button
              type="submit"
              variant="primary"
              size="md"
              fullWidth
              className="mt-6"
            >
              ORDER NOW
            </Button>
          </form>
        </div>
        <div className="relative w-full max-w-[22.625rem] bg-[#f4ecf4] pt-[60px] text-center">
          <div className="sticky top-0 mx-auto my-5 mb-10 max-w-[21rem]">
            <h3 className="mb-10 text-center text-[24px] font-extrabold text-[#95699f]">
              Product Information
            </h3>
            {/* Product 1 */}
            {cartData?.cartList?.map((item) => (
              <div
                key={item.productOption.uuid}
                className="mb-6 border-b border-[#decce1]"
              >
                <div className="flex justify-start">
                  <div className="relative">
                    <img
                      src={item.product.thumbnailUrl}
                      alt="x"
                      className="h-24 w-24 rounded"
                    />
                    <p className="absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded bg-[#fcfcfc72] py-1 text-[12px] font-semibold text-[#444]">
                      {item.quantity}
                    </p>
                  </div>
                  <div className="ml-3 text-left">
                    <p className="mb-2 line-clamp-2 max-w-[228px] overflow-hidden text-ellipsis text-[14px] font-medium leading-[1.6] text-[#444]">
                      {item.product.title}
                    </p>
                    <p className="text-[14px] font-bold leading-[1.4] text-[#b995be]">
                      Option : {item.productOption.optionNameValue1}{' '}
                      {item.productOption.optionNameValue2
                        ? ` / ${item.productOption.optionNameValue2}`
                        : ''}
                    </p>
                  </div>
                </div>
                <div className="my-4 flex justify-between">
                  <p className="text-[14px] font-bold text-[#666]">SubTotal</p>
                  <span className="text-[14px] font-bold text-[#95699f]">
                    {formatNumber(item.quantity)} x{' '}
                    {formatPrice(
                      item.productPrice.price,
                      cartData.currencyCode,
                    )}
                    {cartData.currencySymbol} ={' '}
                    {formatPrice(
                      getTotalPrice(
                        item.productPrice.price,
                        item.quantity,
                        cartData.currencyCode,
                      ),
                      cartData.currencyCode,
                    )}
                    {cartData.currencySymbol}
                  </span>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between">
              <h4 className="text-[18px] font-semibold text-[#1d1d1d]">
                Total
              </h4>
              <p className="text-[16px] font-bold text-[#95699f]">
                {formatPrice(cartData?.totalPrice || 0, cartData?.currencyCode)}
                {cartData?.currencySymbol}
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Order;
